// Flattens nested telemetry objects into dot-notation keys so each record
// becomes one CSV row, e.g. { deviceInfo: { chipTemp: 35 } } -> "deviceInfo.chipTemp".
// Arrays of objects (assetData, cellTowerInfo, serverMetrics, ...) expand into
// indexed columns like "cellTowerInfo[0].rssi"; arrays of primitives (gps,
// nwInfo, ...) are joined with ";" to stay in a single cell.
const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value)

const flattenValue = (value, path, out) => {
  if (isPlainObject(value)) {
    flattenRecord(value, path, out)
  } else if (Array.isArray(value)) {
    if (value.some((item) => item !== null && typeof item === "object")) {
      value.forEach((item, index) => flattenValue(item, `${path}[${index}]`, out))
    } else {
      out[path] = value.join(";")
    }
  } else {
    out[path] = value
  }
}

const flattenRecord = (obj, prefix = "", out = {}) => {
  Object.entries(obj ?? {}).forEach(([key, value]) => {
    flattenValue(value, prefix ? `${prefix}.${key}` : key, out)
  })
  return out
}

const escapeCell = (value) => {
  if (value === null || value === undefined) return ""
  const str = String(value)
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

// records: array of (possibly nested) objects. Column set is the union across
// all records so rows with missing fields still line up.
export const recordsToCsv = (records) => {
  const rows = records.map((record) => flattenRecord(record))
  const headers = [...new Set(rows.flatMap((row) => Object.keys(row)))]
  const lines = [
    headers.map(escapeCell).join(","),
    ...rows.map((row) => headers.map((header) => escapeCell(row[header])).join(",")),
  ]
  return lines.join("\n")
}

// Rows per chunk. Small enough that no single string gets large, big enough
// that the yield between chunks isn't the dominant cost.
const CHUNK_ROWS = 5000

// Above this many rows, callers ask the user before spending the time.
export const CSV_CONFIRM_THRESHOLD = 50000

// Builds the CSV as a list of chunk strings handed straight to Blob, which
// accepts an array of parts — so a contiguous JS string of the whole file is
// never allocated. That sidesteps V8's ~537M-character string ceiling entirely
// and keeps peak heap at roughly one chunk instead of the whole export.
//
// `getLine(index)` must return one already-escaped CSV line. It is called in
// index order, so callers can map their source data a chunk at a time rather
// than materialising every row up front.
//
// Async, with a macrotask yield between chunks: a 500k-row export otherwise
// blocks paint for seconds and the tab looks hung.
export const buildCsvBlob = async ({ headerLine, rowCount, getLine, onProgress }) => {
  // Escape form rather than a literal BOM character, which is invisible in an
  // editor and easily stripped. Same purpose as downloadCsv's: Excel decodes
  // the file as UTF-8 so "°C" doesn't render as "Â°C".
  const parts = ['\ufeff']
  if (headerLine) parts.push(headerLine)

  for (let start = 0; start < rowCount; start += CHUNK_ROWS) {
    const end = Math.min(start + CHUNK_ROWS, rowCount)
    const lines = []
    for (let i = start; i < end; i += 1) lines.push(getLine(i))
    // Leading newline joins this chunk to the previous part.
    parts.push('\n' + lines.join('\n'))
    onProgress?.(end, rowCount)
    // Let the browser paint between chunks.
    await new Promise((resolve) => setTimeout(resolve, 0))
  }

  return new Blob(parts, { type: 'text/csv;charset=utf-8;' })
}

export const escapeCsvCell = escapeCell

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Kept for records whose values are nested objects/arrays — recordsToCsv above
// flattens those into dot-notation columns. The telemetry exports don't need it
// (their cells are already scalar strings) and use buildCsvBlob instead.
export const downloadCsv = (csv, filename) => {
  // Prepend a UTF-8 BOM so Excel decodes the file as UTF-8 instead of
  // Windows-1252 (otherwise "°C" renders as "Â°C", "±" as "Â±", etc.).
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
